<template>
  <div class="cart-page">
    <div class="container">
      <h1 class="cart-title">Cart</h1>

      <div v-if="cartItems.length === 0" class="empty-cart">
        <i class="fas fa-shopping-cart"></i>
        <h2>Your cart is empty</h2>
        <p>Add some products to get started!</p>
        <a href="/" class="btn-primary">Continue Shopping</a>
      </div>

      <div v-else class="cart-content">
        <div class="cart-items">
          <!-- Desktop Header -->
          <div class="cart-header desktop-only">
          </div>

          <div v-for="item in cartItems" :key="item.id" class="cart-item">
            <div class="item-image">
              <a :href="`/${item.product?.slug}`">
                <img :src="getProductImage(item.product?.main_image)" :alt="item.product?.name">
              </a>
            </div>

            <div class="item-details">
              <a :href="`/${item.product?.slug}`" class="item-name">
                <h3>{{ item.product?.name }}</h3>
              </a>
              <p v-if="item.variant" class="variant-info">
                {{ item.variant.variant_name }}
              </p>
              <p class="item-price">
                ₦{{ formatPrice(getItemUnitPrice(item)) }}
                <span v-if="getItemOldPrice(item)" class="old-price">₦{{ formatPrice(getItemOldPrice(item)) }}</span>
              </p>
            </div>

            <div class="item-actions">
              <!-- Quantity Controls -->
              <div class="quantity-controls">
                <button
                  @click="updateQuantity(item.id, item.quantity - 1)"
                  :disabled="item.quantity <= 1 || updating[item.id]"
                  class="qty-btn"
                >
                  −
                </button>
                <input
                  type="number"
                  :value="item.quantity"
                  @change="updateQuantity(item.id, $event.target.value)"
                  :disabled="updating[item.id]"
                  min="1"
                  class="qty-input"
                >
                <button
                  @click="updateQuantity(item.id, item.quantity + 1)"
                  :disabled="updating[item.id]"
                  class="qty-btn"
                >
                  +
                </button>
              </div>

              <!-- Remove Link -->
              <button
                @click="removeItem(item.id)"
                :disabled="removing[item.id]"
                class="btn-remove-link"
              >
                {{ removing[item.id] ? 'Removing...' : 'Remove' }}
              </button>
            </div>

            <div class="item-subtotal">
              <p class="subtotal-amount">₦{{ formatPrice(getItemUnitPrice(item) * item.quantity) }}</p>
            </div>
          </div>
        </div>

        <div class="cart-summary">
          <!-- Coupon Section -->
          <div class="coupon-section">
            <h4>Have a Coupon?</h4>
            <div v-if="!appliedCoupon" class="coupon-input-group">
              <input
                v-model="couponCode"
                type="text"
                placeholder="Enter coupon code"
                :disabled="applyingCoupon"
                class="coupon-input"
                @keyup.enter="applyCoupon"
              >
              <button
                @click="applyCoupon"
                :disabled="!couponCode || applyingCoupon"
                class="btn-apply-coupon"
              >
                <i v-if="applyingCoupon" class="fas fa-spinner fa-spin"></i>
                <span v-else>Apply</span>
              </button>
            </div>

            <div v-else class="applied-coupon">
              <div class="coupon-badge">
                <i class="fas fa-tag"></i>
                <span>{{ appliedCoupon.code }}</span>
                <button @click="removeCoupon" class="btn-remove-coupon" title="Remove coupon">
                  <i class="fas fa-times"></i>
                </button>
              </div>
              <p class="coupon-savings">
                You're saving ₦{{ formatPrice(discountAmount) }}!
              </p>
            </div>
          </div>

          <div class="summary-totals">
            <div class="summary-row total">
              <span>Total</span>
              <span class="total-amount">₦{{ formatPrice(grandTotal) }}</span>
            </div>

            <div v-if="discountAmount > 0" class="summary-row discount">
              <span>Discount ({{ appliedCoupon.code }})</span>
              <span>-₦{{ formatPrice(discountAmount) }}</span>
            </div>

            <p class="shipping-note">Shipping &amp; taxes calculated at checkout</p>
          </div>

          <a href="/checkout" class="btn-checkout">
            <i class="fas fa-lock"></i>
            CHECKOUT
          </a>

          <a href="/" class="btn-continue-shopping">
            <i class="fas fa-arrow-left"></i>
            Continue Shopping
          </a>

        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'

const props = defineProps({
  cart: {
    type: Object,
    default: null
  }
})

const cartItems = ref(props.cart?.cart_items || [])
const updating = ref({})
const removing = ref({})
const couponCode = ref('')
const appliedCoupon = ref(null)
const applyingCoupon = ref(false)
const csrfToken = computed(() => document.querySelector('meta[name="csrf-token"]')?.content || '')

// Check for applied coupon from session on mount
const checkAppliedCoupon = async () => {
  try {
    const response = await fetch('/cart/summary', {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      }
    })

    const data = await response.json()
    if (data.coupon) {
      appliedCoupon.value = data.coupon
    }
  } catch (error) {
    // Coupon check failed silently
  }
}

onMounted(() => {
  checkAppliedCoupon()
})

const cartTotal = computed(() => {
  return cartItems.value.reduce((total, item) => {
    return total + (getItemUnitPrice(item) * item.quantity)
  }, 0)
})

const discountAmount = computed(() => {
  if (!appliedCoupon.value) return 0

  const coupon = appliedCoupon.value
  let discount = 0

  if (coupon.type === 'percentage') {
    discount = (cartTotal.value * coupon.value) / 100
  } else {
    discount = coupon.value
  }

  // Ensure discount doesn't exceed cart total
  return Math.min(discount, cartTotal.value)
})

const grandTotal = computed(() => {
  return cartTotal.value - discountAmount.value
})

const getProductImage = (imagePath) => {
  if (!imagePath) return '/images/placeholder.jpg'
  return imagePath
}

const formatPrice = (price) => {
  return new Intl.NumberFormat('en-NG').format(price || 0)
}

const getItemUnitPrice = (item) => {
  return Number(
    item?.variant?.base_price ??
    item?.product?.base_price ??
    item?.price ??
    0
  )
}

const getItemOldPrice = (item) => {
  const basePrice = Number(item?.variant?.base_price ?? item?.product?.base_price ?? 0)
  const comparePrice = Number(item?.variant?.compare_price ?? item?.product?.compare_price ?? 0)

  if (comparePrice > basePrice && basePrice > 0) return comparePrice
  return null
}

const updateQuantity = async (itemId, newQuantity) => {
  if (newQuantity < 1) return

  updating.value[itemId] = true

  try {
    const formData = new FormData()
    formData.append('_token', csrfToken.value)
    formData.append('quantity', newQuantity)

    const response = await fetch(`/cart/${itemId}/update`, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      },
      body: formData
    })

    const data = await response.json()

    if (data.success) {
      // Update local state
      const item = cartItems.value.find(i => i.id === itemId)
      if (item) {
        item.quantity = newQuantity
      }

      // Update cart badge
      updateCartBadge(data.cart_count)

      if (window.Swal) {
        window.Swal.fire({
          icon: 'success',
          title: 'Updated!',
          text: 'Cart updated successfully',
          timer: 1500,
          showConfirmButton: false,
          position: 'top-end',
          toast: true
        })
      }
    } else {
      throw new Error(data.message || 'Failed to update cart')
    }
  } catch (error) {
    if (window.Swal) {
      window.Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to update quantity',
        confirmButtonColor: '#0063d1'
      })
    } else {
      alert('Failed to update quantity')
    }
  } finally {
    updating.value[itemId] = false
  }
}

const removeItem = async (itemId) => {
  const confirmed = window.confirm('Are you sure you want to remove this item?')
  if (!confirmed) return

  removing.value[itemId] = true

  try {
    const formData = new FormData()
    formData.append('_token', csrfToken.value)

    const response = await fetch(`/cart/${itemId}/remove`, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      },
      body: formData
    })

    const data = await response.json()

    if (data.success) {
      // Remove from local state
      cartItems.value = cartItems.value.filter(i => i.id !== itemId)

      // Update cart badge
      updateCartBadge(data.cart_count)

      if (window.Swal) {
        window.Swal.fire({
          icon: 'success',
          title: 'Removed!',
          text: 'Item removed from cart',
          timer: 1500,
          showConfirmButton: false,
          position: 'top-end',
          toast: true
        })
      }

      // Reload page if cart is empty
      if (cartItems.value.length === 0) {
        setTimeout(() => window.location.reload(), 1500)
      }
    } else {
      throw new Error(data.message || 'Failed to remove item')
    }
  } catch (error) {
    if (window.Swal) {
      window.Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to remove item',
        confirmButtonColor: '#0063d1'
      })
    } else {
      alert('Failed to remove item')
    }
  } finally {
    removing.value[itemId] = false
  }
}

const updateCartBadge = (count) => {
  // Dispatch event to update cart count in AppHeader
  window.dispatchEvent(new CustomEvent('cart-updated', {
    detail: { count: count }
  }))
}

const applyCoupon = async () => {
  if (!couponCode.value) return

  applyingCoupon.value = true

  try {
    const formData = new FormData()
    formData.append('_token', csrfToken.value)
    formData.append('code', couponCode.value.toUpperCase())

    const response = await fetch('/cart/coupon/apply', {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      },
      body: formData
    })

    const data = await response.json()

    if (response.ok && data.success) {
      appliedCoupon.value = data.coupon
      couponCode.value = ''

      if (window.Swal) {
        window.Swal.fire({
          icon: 'success',
          title: 'Coupon Applied!',
          text: data.message || 'Discount applied successfully',
          timer: 2000,
          showConfirmButton: false,
          position: 'top-end',
          toast: true
        })
      }
    } else {
      throw new Error(data.message || 'Failed to apply coupon')
    }
  } catch (error) {
    if (window.Swal) {
      window.Swal.fire({
        icon: 'error',
        title: 'Invalid Coupon',
        text: error.message || 'This coupon code is not valid',
        confirmButtonColor: '#F55203'
      })
    } else {
      alert(error.message || 'Failed to apply coupon')
    }
  } finally {
    applyingCoupon.value = false
  }
}

const removeCoupon = async () => {
  try {
    const formData = new FormData()
    formData.append('_token', csrfToken.value)

    const response = await fetch('/cart/coupon/remove', {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      },
      body: formData
    })

    const data = await response.json()

    if (data.success) {
      appliedCoupon.value = null
      couponCode.value = ''

      if (window.Swal) {
        window.Swal.fire({
          icon: 'info',
          title: 'Coupon Removed',
          text: 'Discount has been removed',
          timer: 1500,
          showConfirmButton: false,
          position: 'top-end',
          toast: true
        })
      }
    } else {
      throw new Error(data.message || 'Failed to remove coupon')
    }
  } catch (error) {
    // Remove coupon failed silently
  }
}
</script>
